import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Program, School, Country, Subject } from 'app/types';
import Text from 'common/components/Text/Text';
import {
  useGetSchoolsQuery,
  useGetProgramsQuery,
  useGetCountriesQuery,
  useGetSubjectsQuery,
} from 'services/exploreApi';
import ProgramCard from 'features/user/cards/ProgramCard';
import SchoolCard from 'features/user/cards/SchoolCard';
import DestinationCard from 'features/user/cards/DestinationCard';
import { SessionState, setFilters } from 'features/session/sessionSlice';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { navigateTop } from 'common/utils';
import Loader from 'common/components/Loader/Loader';
import styles from 'features/user/dashboard/pages/dashboard/Dashboard.module.scss';

const ExploreTopic = (): React.ReactElement => {
  const { profile } = useAppSelector(({ session }: { session: SessionState }) => session);
  const { data: schools, isLoading: schoolsLoading } = useGetSchoolsQuery(
    profile ? { order_by_scoring: 'desc' } : null
  );
  const { data: programs, isLoading: programsLoading } = useGetProgramsQuery(
    profile ? { order_by_scoring: 'desc' } : null
  );
  const { data: countries, isLoading: countriesLoading } = useGetCountriesQuery(
    profile ? { order_by_scoring: 'desc' } : null
  );
  const { data: subjects, isLoading: subjectsLoading } = useGetSubjectsQuery(
    profile ? { order_by_scoring: 'desc' } : null
  );

  const [searchQuery, setSearchQuery] = useState<string>('');
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  const handleSearch = (event: React.KeyboardEvent<HTMLDivElement>, search = false) => {
    if (search || event.key === 'Enter') {
      dispatch(setFilters({ query: searchQuery }));
      navigate('/explore/programs');
      navigateTop();
    }
  };
  const handleSubjectFilter = (subject_id: number) => {
    dispatch(setFilters({ subject_id: [subject_id] }));
    navigate('/explore/programs');
    navigateTop();
  };

  return (
    <div>
      <div className={styles.section}>
        <div className={styles.rowTitle}>
          <Text size={isMobile ? 16 : 20} weight={600} inline>
            Recommended Programs
          </Text>
          <div
            className={styles.seeMoreRow}
            onClick={() => {
              navigate('/explore/programs'), navigateTop();
            }}
          >
            See more <i className="bi bi-arrow-right"></i>
          </div>
        </div>
        <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-3 pt-1`}>
          {programsLoading ? (
            <Loader />
          ) : (
            programs?.data.map((program: Program) => (
              <ProgramCard program={program} key={program.id} />
            ))
          )}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.rowTitle}>
          <Text size={isMobile ? 16 : 20} weight={600} inline>
            Recommended Schools
          </Text>
          <div
            className={styles.seeMoreRow}
            onClick={() => {
              navigate('/explore'), navigateTop();
            }}
          >
            See more <i className="bi bi-arrow-right"></i>
          </div>
        </div>
        <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-3 pt-1`}>
          {schoolsLoading ? (
            <Loader />
          ) : (
            schools?.data.map((school: School) => <SchoolCard school={school} key={school.id} />)
          )}
        </div>
      </div>
      <div className={styles.section}>
        <Text size={isMobile ? 16 : 20} weight={600}>
          Recommended Destinations
        </Text>
        <div className={`${styles.scrollingWrapper} row flex-row flex-nowrap mt-3 pt-1`}>
          {countriesLoading ? (
            <Loader />
          ) : (
            countries?.data.map((country: Country) => (
              <DestinationCard country={country} key={country.id} />
            ))
          )}
        </div>
      </div>
      <div className={styles.section}>
        <Text size={isMobile ? 16 : 20} weight={600}>
          Recommended Subjects
        </Text>
        <div className={styles.subjectsContainer}>
          {subjectsLoading ? (
            <Loader />
          ) : (
            subjects?.data.slice(0, isMobile ? 5 : 10).map((subject: Subject) => (
              <div
                className={styles.subjectTag}
                key={subject.id}
                onClick={() => handleSubjectFilter(subject.id)}
              >
                {subject.name}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
export default ExploreTopic;
